import Portal from "../portal";
import useKeyPress from "../../utils/hooks/use-key-press";
import cx from "../../utils/tailwind-merge";
import { createContext, useContext, useMemo, useCallback } from "react";

const DrawerContext = createContext({});

const Drawer = (props) => {
  const {
    children,
    onOverlayClick,
    onEscapeClick,
    classNames = {},
    className,
    closeOnOverlay = true,
    closeOnEscape = true,
    value,
    setIsOpen,
    ...restProps
  } = props;

  const handleClose = useCallback(() => {
    setIsOpen(() => false);
  }, [setIsOpen]);

  const handleOverlayClick = useCallback(() => {
    if (onOverlayClick) {
      onOverlayClick();
    } else {
      closeOnOverlay && setIsOpen(() => false);
    }
  }, [closeOnOverlay, onOverlayClick, setIsOpen]);

  useKeyPress(document, "Escape", (e) => {
    onEscapeClick && onEscapeClick(e);
    if (onEscapeClick) {
      onEscapeClick(e);
    } else {
      closeOnEscape && setIsOpen(() => false);
    }
  });

  const values = useMemo(() => {
    return {
      open: value,
      onClose: handleClose,
    };
  }, [handleClose, value]);

  return (
    <Portal>
      {value ? (
        <DrawerContext.Provider value={values}>
          <div
            className={cx("drawer z-[1000] fixed top-0 left-0 w-full h-full overflow-hidden bg-[black]/30 ", classNames.root, className)}
            {...restProps}
          >
            <div className={cx("h-full w-1/3 bg-white float-right pb-4", classNames.content)}>{children}</div>
            <div
              role="button"
              tabIndex={0}
              className={cx("z-[-1] h-full w-full bg-secondary-900 bg-opacity-25", classNames.overlay)}
              onClick={handleOverlayClick}
            ></div>
          </div>
        </DrawerContext.Provider>
      ) : null}
    </Portal>
  );
};

export function useDrawer() {
  const context = useContext(DrawerContext);
  if (context === undefined) {
    throw new Error("useDrawer must be used within a Drawer");
  }
  return context;
}

export default Drawer;
